(async () => {
  // Measure only after the custom astronomical glyph is available.
  await document.fonts.load('100px "Astro Jupiter"', "\u2643").catch(() => {});
  const SVG_NS = "http://www.w3.org/2000/svg";
  const AYANAMSA = 23.398;
  const GROUP_THRESHOLD = 20;
  const MIN_SPACING = 16;
  const SIDEREAL_HOUSE_ROTATION = -60;
  const ORB_ASPECTS = [
    { degrees: 0, orb: 9, className: "soft" },
    { degrees: 60, orb: 5, className: "blue" },
    { degrees: 72, orb: 2, className: "quintile" },
    { degrees: 90, orb: 9, className: "red" },
    { degrees: 120, orb: 9, className: "blue" },
    { degrees: 150, orb: 3, className: "green" },
    { degrees: 180, orb: 9, className: "red" }
  ];

  const PLANETS = {
    Sun: 229.68,
    Moon: 248.096,
    Mercury: 250.276,
    Venus: 229.955,
    Mars: 55.225,
    Jupiter: 223.753,
    Saturn: 263.818,
    Uranus: 136.325,
    Neptune: 215.096,
    Pluto: 154.051
  };

  // Placidus cusps for the same reference chart used by the hero planets
  // (12 November 1958, 15:00, Palma). Keeping both datasets together avoids
  // presenting equal-house guides over a chart calculated with real cusps.
  const TROPICAL_HOUSES = [
    349.582647,
    33.133013,
    61.9561549,
    84.3473864,
    105.8258768,
    131.4020057,
    169.582647,
    213.133013,
    241.9561549,
    264.3473864,
    285.8258768,
    311.4020057
  ];
  const MEMORY_HOUSE_SOURCE = [7, 6, 5, 4, 3, 2, 1, 12, 11, 10, 9, 8];
  const IDENTITY_MATRIX = { a: 1, b: 0, c: 0, d: 1 };
  const MEMORY_WHEEL_MATRIX = {
    a: -Math.cos(Math.PI / 6),
    b: 0.5,
    c: 0.5,
    d: Math.cos(Math.PI / 6)
  };

  const PLANET_GLYPHS = {
    Sun: "\u2609",
    Moon: "\u263D",
    Mercury: "\u263F",
    Venus: "\u2640",
    Mars: "\u2642",
    Jupiter: "\u2643",
    Saturn: "\u2644",
    Uranus: "\u2645",
    Neptune: "\u2646",
    Pluto: "\u2647",
    Earth: "\u2295"
  };

  const PLANET_COLORS = {
    Sun: "#ff0000",
    Moon: "#ff0000",
    Mercury: "#1e4f8f",
    Venus: "#1e4f8f",
    Mars: "#1e4f8f",
    Jupiter: "#2f8f46",
    Saturn: "#8b5a2b",
    Uranus: "#f505f0",
    Neptune: "#f505f0",
    Pluto: "#f505f0",
    Earth: "#ff8c00"
  };

  function normalize(longitude) {
    return ((Number(longitude) % 360) + 360) % 360;
  }

  function shiftedPlanets(offset) {
    const planets = Object.fromEntries(
      Object.entries(PLANETS).map(([key, longitude]) => [key, normalize(longitude - offset)])
    );
    planets.Earth = normalize(planets.Sun + 180);
    return planets;
  }

  function arrangePlanets(planets) {
    const sorted = Object.entries(planets)
      .map(([key, longitude]) => ({ key, longitude }))
      .sort((a, b) => a.longitude - b.longitude);
    const groups = [];

    sorted.forEach((planet) => {
      const current = groups[groups.length - 1];
      const previous = current?.[current.length - 1];
      if (!current || planet.longitude - previous.longitude > GROUP_THRESHOLD) {
        groups.push([planet]);
      } else {
        current.push(planet);
      }
    });

    if (groups.length > 1) {
      const first = groups[0];
      const last = groups[groups.length - 1];
      if (first[0].longitude + 360 - last[last.length - 1].longitude <= GROUP_THRESHOLD) {
        groups[groups.length - 1] = [...last, ...first];
        groups.shift();
      }
    }

    return groups.flatMap((group) => {
      if (group.length === 1) {
        return [{ ...group[0], displayLongitude: group[0].longitude }];
      }

      const unwrapped = [];
      group.forEach((planet, index) => {
        let longitude = planet.longitude;
        if (index > 0) {
          while (longitude <= unwrapped[index - 1]) longitude += 360;
        }
        unwrapped.push(longitude);
      });

      const naturalSpan = unwrapped[unwrapped.length - 1] - unwrapped[0];
      const spacing = Math.max(MIN_SPACING, naturalSpan / Math.max(1, group.length - 1));
      const center = unwrapped.reduce((sum, longitude) => sum + longitude, 0) / unwrapped.length;

      return group.map((planet, index) => ({
        ...planet,
        displayLongitude: normalize(center + (index - ((group.length - 1) / 2)) * spacing)
      }));
    });
  }

  function chartPoint(longitude, radius, mode, center, rotation = 0) {
    const baseAngle = mode === "memory" ? longitude - 30 : 180 - longitude;
    const angle = (baseAngle + rotation) * Math.PI / 180;
    return {
      x: Number((center + Math.cos(angle) * radius).toFixed(1)),
      y: Number((center + Math.sin(angle) * radius).toFixed(1))
    };
  }

  function svgElement(tag, attributes = {}, text = "") {
    const element = document.createElementNS(SVG_NS, tag);
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, String(value)));
    if (text) element.textContent = text;
    return element;
  }

  function houseMidpoint(longitude, nextLongitude, mode) {
    const distance = mode === "memory"
      ? normalize(longitude - nextLongitude)
      : normalize(nextLongitude - longitude);
    return normalize(longitude + (mode === "memory" ? -distance / 2 : distance / 2));
  }

  function renderHeroHouseAnnotations(mapSelector, houses, mode, rotation = 0) {
    const layer = document.querySelector(`${mapSelector} .aa-house-annotation-layer`);
    if (!layer || houses.length !== 12) return;

    const cuspLines = svgElement("g", { class: "aa-house-cusp-lines aa-hero-house-cusps" });
    const houseNumbers = svgElement("g", {
      class: "aa-house-number aa-hero-house-numbers",
      "text-anchor": "middle",
      "dominant-baseline": "middle"
    });
    const extensionCommands = [];

    houses.forEach((longitude, index) => {
      const houseNumber = index + 1;
      const nextLongitude = houses[(index + 1) % houses.length];
      const cuspInner = chartPoint(longitude, 118, mode, 0, rotation);
      const cuspOuter = chartPoint(longitude, 210, mode, 0, rotation);
      const extensionInner = chartPoint(longitude, 188, mode, 0, rotation);
      const extensionOuter = chartPoint(longitude, 216, mode, 0, rotation);
      const numberPoint = chartPoint(houseMidpoint(longitude, nextLongitude, mode), 206, mode, 0, rotation);
      // All cusps reach the inner rim with the same neutral styling.
      cuspLines.append(svgElement("line", {
        x1: cuspInner.x,
        y1: cuspInner.y,
        x2: cuspOuter.x,
        y2: cuspOuter.y,
        class: "aa-house-cusp-line",
        "data-house": houseNumber,
        "data-longitude": Number(longitude).toFixed(6)
      }));
      extensionCommands.push(`M${extensionInner.x} ${extensionInner.y}L${extensionOuter.x} ${extensionOuter.y}`);
      houseNumbers.append(svgElement("text", {
        x: numberPoint.x,
        y: numberPoint.y,
        "data-house": houseNumber
      }, ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"][index]));
    });

    layer.replaceChildren(
      cuspLines,
      svgElement("path", { class: "aa-house-cusp-extensions", d: extensionCommands.join(" ") }),
      houseNumbers
    );
  }

  function rotationMatrix(degrees) {
    const radians = degrees * Math.PI / 180;
    return {
      a: Math.cos(radians),
      b: Math.sin(radians),
      c: -Math.sin(radians),
      d: Math.cos(radians)
    };
  }

  function multiplyMatrices(left, right) {
    return {
      a: left.a * right.a + left.c * right.b,
      b: left.b * right.a + left.d * right.b,
      c: left.a * right.c + left.c * right.d,
      d: left.b * right.c + left.d * right.d
    };
  }

  function invertMatrix(matrix) {
    const determinant = matrix.a * matrix.d - matrix.b * matrix.c;
    return {
      a: matrix.d / determinant,
      b: -matrix.b / determinant,
      c: -matrix.c / determinant,
      d: matrix.a / determinant
    };
  }

  function cssMatrix(matrix) {
    const values = [matrix.a, matrix.b, matrix.c, matrix.d, 0, 0]
      .map((value) => Number(value.toFixed(8)));
    return `matrix(${values.join(", ")})`;
  }

  function orientHeroWheel(mapSelector, rotation, baseMatrix = IDENTITY_MATRIX) {
    const wheelTone = document.querySelector(`${mapSelector} .aa-dark-map-wheel-tone`);
    const wheel = wheelTone?.querySelector("use");
    const signOverlay = document.querySelector(`${mapSelector} .aa-dark-map-sign-overlay`);
    const totalMatrix = multiplyMatrices(rotationMatrix(rotation), baseMatrix);

    if (wheelTone) wheelTone.setAttribute("transform", `rotate(${rotation})`);
    if (wheel) {
      wheel.style.setProperty("--landing-wheel-text-transform", cssMatrix(invertMatrix(totalMatrix)));
    }

    if (signOverlay) {
      signOverlay.setAttribute("transform", `rotate(${rotation})`);
      signOverlay.style.setProperty("--aa-hero-sign-counter-transform", `rotate(${-rotation}deg)`);
    }
  }

  function textAttributes(point, className) {
    return {
      x: point.x,
      y: point.y,
      class: className,
      "text-anchor": "middle",
      "dominant-baseline": "middle"
    };
  }

  function layoutPlanet(planet, config) {
    const { key, longitude, displayLongitude } = planet;
    const trueInner = chartPoint(longitude, config.radii.markInner, config.mode, config.center, config.rotation);
    const trueOuter = chartPoint(longitude, config.radii.markOuter, config.mode, config.center, config.rotation);
    const glyph = chartPoint(displayLongitude, config.radii.glyph, config.mode, config.center, config.rotation);

    return {
      ...planet,
      color: PLANET_COLORS[key],
      glyphText: PLANET_GLYPHS[key],
      trueInner,
      trueOuter,
      glyph,
      major: key === "Sun" || key === "Moon" || key === "Earth"
    };
  }

  const glyphScales = new Map();
  function heroGlyphStyle(item, scale = 1) {
    if (!glyphScales.has(item.glyphText)) {
      const context = document.createElement("canvas").getContext("2d");
      if (context) context.font = '400 100px "Astro Jupiter", Arial, "Segoe UI Symbol", "Noto Sans Symbols 2", sans-serif';
      const extent = (glyph) => {
        const metrics = context?.measureText(glyph);
        return Math.max(
          (metrics?.actualBoundingBoxLeft || 0) + (metrics?.actualBoundingBoxRight || 0),
          (metrics?.actualBoundingBoxAscent || 0) + (metrics?.actualBoundingBoxDescent || 0)
        ) || 72;
      };
      const reference = (extent(PLANET_GLYPHS.Sun) + extent(PLANET_GLYPHS.Moon)) / 2;
      glyphScales.set(item.glyphText, reference / extent(item.glyphText));
    }
    const fontSize = 20.7 * glyphScales.get(item.glyphText) / scale;
    const stroke = item.key === "Venus" || item.key === "Mars" ? "0.065em" : `${0.23 / scale}px`;
    return `--planet-color:${item.color};font-family: "Astro Jupiter", Arial,"Segoe UI Symbol","Noto Sans Symbols 2",sans-serif;font-weight:400;font-size:${fontSize.toFixed(3)}px!important;fill:var(--hero-glyph-color,${item.color})!important;stroke:var(--hero-glyph-color,${item.color})!important;stroke-width:${stroke};paint-order:stroke fill`;
  }

  function renderTropicalPlanets(planets) {
    const container = document.querySelector("#landingTechnicalTropicalWheel > .landing-map-planet-labels");
    if (!container) return;

    const config = {
      mode: "normal",
      center: 210,
      radii: {
        markInner: 104,
        markOuter: 118,
        glyph: 88
      }
    };

    const nodes = arrangePlanets(planets).map((planet) => {
      const item = layoutPlanet(planet, config);
      const group = svgElement("g", {
        class: `chart-planet chart-planet-natal landing-map-planet-label-${item.key.toLowerCase()} aa-hero-planet-label`,
        style: `--planet-color:${item.color}`,
        "data-planet": item.key
      });

      group.append(
        svgElement("line", {
          x1: item.trueInner.x, y1: item.trueInner.y,
          x2: item.trueOuter.x, y2: item.trueOuter.y,
          class: "landing-map-planet-degree-mark"
        }),
        svgElement("line", {
          x1: item.trueInner.x, y1: item.trueInner.y,
          x2: item.glyph.x, y2: item.glyph.y,
          class: "landing-map-planet-connector"
        }),
        svgElement("text", { ...textAttributes(
          item.glyph,
          `aa-hero-optical-glyph chart-planet-glyph${item.major ? " chart-planet-glyph-major" : ""}`
        ), style: heroGlyphStyle(item) }, item.glyphText)
      );
      return group;
    });

    container.replaceChildren(...nodes);
  }

  function renderCustomPlanets(layerSelector, planets, mode, scale = 1.086956, rotation = 0) {
    const layer = document.querySelector(layerSelector);
    const guides = layer?.querySelector(".aa-sideral-planet-guides");
    const labels = layer?.querySelector(".aa-sideral-planet-labels");
    if (!guides || !labels) return;

    const config = {
      mode,
      center: 0,
      rotation,
      radii: {
        markInner: 104 / scale,
        markOuter: 118 / scale,
        glyph: 88 / scale
      }
    };

    const guideNodes = [];
    const labelNodes = [];

    arrangePlanets(planets).forEach((planet) => {
      const item = layoutPlanet(planet, config);
      const guide = svgElement("g", {
        style: `--planet-color:${item.color}`,
        "data-planet": item.key
      });
      guide.append(
        svgElement("line", {
          x1: item.trueInner.x, y1: item.trueInner.y,
          x2: item.trueOuter.x, y2: item.trueOuter.y,
          class: "aa-sideral-planet-degree-mark"
        }),
        svgElement("line", {
          x1: item.trueInner.x, y1: item.trueInner.y,
          x2: item.glyph.x, y2: item.glyph.y,
          class: "aa-sideral-planet-connector"
        })
      );
      guideNodes.push(guide);

      labelNodes.push(
        svgElement("text", {
          ...textAttributes(
            item.glyph,
            `aa-hero-optical-glyph aa-sideral-planet-glyph aa-hero-planet-glyph${item.major ? " aa-hero-planet-glyph-major" : ""}`
          ),
          style: heroGlyphStyle(item, scale),
          "data-planet": item.key
        }, item.glyphText)
      );
    });

    guides.replaceChildren(...guideNodes);
    labels.replaceChildren(...labelNodes);
  }

  function signAspect(longitudeA, longitudeB) {
    const signA = Math.floor(normalize(longitudeA) / 30);
    const signB = Math.floor(normalize(longitudeB) / 30);
    const rawDistance = Math.abs(signA - signB);
    const signDistance = Math.min(rawDistance, 12 - rawDistance);

    return {
      0: { degrees: 0, className: "aa-sideral-aspect-soft" },
      2: { degrees: 60, className: "aa-sideral-aspect-blue" },
      3: { degrees: 90, className: "aa-sideral-aspect-red" },
      4: { degrees: 120, className: "aa-sideral-aspect-blue" },
      5: { degrees: 150, className: "aa-sideral-aspect-green" },
      6: { degrees: 180, className: "aa-sideral-aspect-red" }
    }[signDistance] || null;
  }

  function orbAspect(longitudeA, longitudeB) {
    const rawDistance = Math.abs(normalize(longitudeA) - normalize(longitudeB));
    const distance = Math.min(rawDistance, 360 - rawDistance);

    for (const aspect of ORB_ASPECTS) {
      const orb = Math.abs(distance - aspect.degrees);
      if (orb <= aspect.orb) return { ...aspect, actualOrb: orb };
    }
    return null;
  }

  function aspectBodies(planets, mode, center, scale = 1, rotation = 0) {
    return Object.entries(planets)
      .filter(([key]) => key !== "Earth")
      .map(([key, longitude]) => ({
        key,
        longitude,
        color: PLANET_COLORS[key],
        inner: chartPoint(longitude, 65 / scale, mode, center, rotation),
        outer: chartPoint(longitude, 76 / scale, mode, center, rotation)
      }));
  }

  function orbAspectLines(bodies, classPrefix) {
    const lines = [];
    bodies.forEach((bodyA, index) => {
      bodies.slice(index + 1).forEach((bodyB) => {
        const aspect = orbAspect(bodyA.longitude, bodyB.longitude);
        if (!aspect) return;

        lines.push(svgElement("line", {
          class: `${classPrefix}-aspect ${classPrefix}-aspect-${aspect.className}`,
          "data-aspect": aspect.degrees,
          "data-orb": aspect.actualOrb.toFixed(3),
          "data-between": `${bodyA.key}-${bodyB.key}`,
          x1: bodyA.inner.x,
          y1: bodyA.inner.y,
          x2: bodyB.inner.x,
          y2: bodyB.inner.y
        }));
      });
    });
    return lines;
  }

  function renderTropicalAspects(planets) {
    const wheel = document.querySelector("#landingTechnicalTropicalWheel");
    const aspects = wheel?.querySelector(".landing-map-aspects");
    const anchors = wheel?.querySelector(".landing-map-aspect-anchors");
    if (!aspects || !anchors) return;

    const bodies = aspectBodies(planets, "normal", 210);
    anchors.replaceChildren(...bodies.map((body) => svgElement("line", {
      class: "landing-map-aspect-anchor",
      style: `--planet-color:${body.color}`,
      "data-planet": body.key,
      x1: body.inner.x,
      y1: body.inner.y,
      x2: body.outer.x,
      y2: body.outer.y
    })));

    [...aspects.children]
      .filter((child) => child.matches("line.landing-map-aspect"))
      .forEach((line) => line.remove());
    aspects.append(...orbAspectLines(bodies, "landing-map"));
  }

  function renderCustomOrbAspects(layerSelector, planets, mode, scale = 1.086956, rotation = 0) {
    const layer = document.querySelector(layerSelector);
    const anchors = layer?.querySelector(".aa-sideral-aspect-anchors");
    const aspects = layer?.querySelector(".aa-sideral-aspects");
    if (!anchors || !aspects) return;

    const bodies = aspectBodies(planets, mode, 0, scale, rotation);
    anchors.removeAttribute("transform");
    aspects.removeAttribute("transform");
    anchors.replaceChildren(...bodies.map((body) => svgElement("line", {
      class: "aa-sideral-aspect-anchor",
      style: `--planet-color:${body.color}`,
      "data-planet": body.key,
      x1: body.inner.x,
      y1: body.inner.y,
      x2: body.outer.x,
      y2: body.outer.y
    })));
    aspects.replaceChildren(...orbAspectLines(bodies, "aa-sideral"));
  }

  function renderSignBasedAspects(layerSelector, planets, mode, scale = 1.086956, rotation = 0) {
    const layer = document.querySelector(layerSelector);
    const anchors = layer?.querySelector(".aa-sideral-aspect-anchors");
    const aspects = layer?.querySelector(".aa-sideral-aspects");
    if (!anchors || !aspects) return;

    const bodies = Object.entries(planets).map(([key, longitude]) => ({
      key,
      longitude,
      color: PLANET_COLORS[key],
      inner: chartPoint(longitude, 65 / scale, mode, 0, rotation),
      outer: chartPoint(longitude, 76 / scale, mode, 0, rotation)
    }));

    anchors.removeAttribute("transform");
    aspects.removeAttribute("transform");
    anchors.replaceChildren(...bodies.map((body) => svgElement("line", {
      class: "aa-sideral-aspect-anchor",
      style: `--planet-color:${body.color}`,
      "data-planet": body.key,
      x1: body.inner.x,
      y1: body.inner.y,
      x2: body.outer.x,
      y2: body.outer.y
    })));

    const aspectLines = [];
    bodies.forEach((bodyA, index) => {
      bodies.slice(index + 1).forEach((bodyB) => {
        const aspect = signAspect(bodyA.longitude, bodyB.longitude);
        if (!aspect) return;

        aspectLines.push(svgElement("line", {
          class: `aa-sideral-aspect ${aspect.className}`,
          "data-aspect": aspect.degrees,
          "data-between": `${bodyA.key}-${bodyB.key}`,
          x1: bodyA.inner.x,
          y1: bodyA.inner.y,
          x2: bodyB.inner.x,
          y2: bodyB.inner.y
        }));
      });
    });
    aspects.replaceChildren(...aspectLines);
  }

  function shiftRingContent(root, ringSelector, segmentSelector, glyphSelector, shift) {
    const ring = root.querySelector(ringSelector);
    if (!ring) return;

    const segments = [...ring.querySelectorAll(segmentSelector)];
    const glyphs = [...root.querySelectorAll(glyphSelector)];
    const segmentStyles = segments.map((segment) => segment.getAttribute("style"));
    const glyphContent = glyphs.map((glyph) => ({
      text: glyph.textContent,
      style: glyph.getAttribute("style")
    }));

    segments.forEach((segment, index) => {
      const sourceIndex = (index + shift) % segments.length;
      const style = segmentStyles[sourceIndex];
      if (style) segment.setAttribute("style", style);
      else segment.removeAttribute("style");
    });

    glyphs.forEach((glyph, index) => {
      const sourceIndex = (index + shift) % glyphs.length;
      const source = glyphContent[sourceIndex];
      glyph.textContent = source.text;
      if (source.style) glyph.setAttribute("style", source.style);
      else glyph.removeAttribute("style");
    });
  }

  function createSideralAquariusWheel() {
    const source = document.querySelector("#landingTechnicalTropicalWheel");
    const wheelUse = document.querySelector(".aa-sideral-technical-wheel");
    if (!source || !wheelUse) return;

    const clone = source.cloneNode(true);
    clone.id = "landingTechnicalSideralAquariusWheel";
    clone.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));

    shiftRingContent(clone, ".chart-zodiac-ring", ".chart-zodiac-segment", ".chart-sign-symbol", 10);
    shiftRingContent(clone, ".chart-decanate-ring", ".chart-decanate-segment", ".chart-decanate-symbol", 30);
    shiftRingContent(clone, ".chart-dodecatemoria-ring", ".chart-dodecatemoria-segment", ".chart-dodecatemoria-symbol", 120);

    source.parentNode.appendChild(clone);
    wheelUse.setAttribute("href", "#landingTechnicalSideralAquariusWheel");
  }

  const tropical = shiftedPlanets(0);
  const sideral = shiftedPlanets(AYANAMSA);
  const memory = shiftedPlanets(AYANAMSA * 2);
  const memoryHouses = MEMORY_HOUSE_SOURCE.map((sourceHouse) => TROPICAL_HOUSES[sourceHouse - 1]);
  const tropicalRotation = TROPICAL_HOUSES[0] - 360;
  const memoryRotation = 210 - memoryHouses[0];

  createSideralAquariusWheel();
  orientHeroWheel("#aaAboutTropicalMap", tropicalRotation);
  orientHeroWheel("#aaAboutMemoryMap", memoryRotation, MEMORY_WHEEL_MATRIX);
  renderHeroHouseAnnotations("#aaAboutTropicalMap", TROPICAL_HOUSES, "normal", tropicalRotation);
  renderHeroHouseAnnotations("#aaAboutMemoryMap", memoryHouses, "memory", memoryRotation);
  renderTropicalPlanets(tropical);
  renderTropicalAspects(tropical);
  renderCustomPlanets(".aa-dark-tropical-contrast-layer", tropical, "normal", 1, tropicalRotation);
  renderCustomOrbAspects(".aa-dark-tropical-contrast-layer", tropical, "normal", 1, tropicalRotation);
  renderCustomPlanets(".aa-sideral-data-layer", sideral, "normal", 1.086956, SIDEREAL_HOUSE_ROTATION);
  renderSignBasedAspects(".aa-sideral-data-layer", sideral, "normal", 1.086956, SIDEREAL_HOUSE_ROTATION);
  renderCustomPlanets(".aa-memory-data-layer", memory, "memory", 1.086956, memoryRotation);
  renderCustomOrbAspects(".aa-memory-data-layer", memory, "memory", 1.086956, memoryRotation);
  
  // Ajuste común para los textos de las ruedas del hero.
function estabilizarTextosDelHero() {
  const selector = [
    "#landingTechnicalTropicalWheel text",
    "#landingTechnicalSideralAquariusWheel text",
    "#aaZodiacNormal text",
    "#aaZodiacMemory text",
    "#aaZodiacAquariusHouseOne text"
  ].join(", ");

  for (const texto of document.querySelectorAll(selector)) {
    // 1. Solicitar los signos como texto, no como emojis.
    // Conserva los <tspan> o <textPath> que pudiera haber.
    const walker = document.createTreeWalker(
      texto,
      NodeFilter.SHOW_TEXT
    );

    let nodo;

    while ((nodo = walker.nextNode())) {
      const corregido = nodo.data.replace(
        /([\u2648-\u2653])[\uFE0E\uFE0F]*/g,
        "$1\uFE0E"
      );

      if (corregido !== nodo.data) {
        nodo.data = corregido;
      }
    }

    // 2. Fijar un punto de giro explícito para los textos
    // que tienen una única posición x/y.
    const posicionesX = texto.x.baseVal;
    const posicionesY = texto.y.baseVal;

    if (
      posicionesX.numberOfItems !== 1 ||
      posicionesY.numberOfItems !== 1
    ) {
      continue;
    }

    const x = posicionesX.getItem(0).value;
    const y = posicionesY.getItem(0).value;

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      continue;
    }

    texto.style.setProperty("transform-box", "view-box");
    texto.style.setProperty(
      "transform-origin",
      `${x}px ${y}px`
    );
  }
}

estabilizarTextosDelHero();
  
})();
